import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { useTheme } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, Moon, Sun, History, FileText, 
  TrendingUp, Calendar, Building2, Repeat, Euro, ExternalLink, Car
} from "lucide-react";
import logoMcom from "@/assets/logo192.png";
import storage from "@/utils/storage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryPage() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [session, setSession] = useState(null);
  const [vnCalculations, setVnCalculations] = useState([]);
  const [voCalculations, setVoCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("vn");

  useEffect(() => {
    const parsedSession = storage.getJSON("emcom_session");
    if (parsedSession) {
      setSession(parsedSession);
      fetchHistory(parsedSession.concession_id);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  const fetchHistory = async (concessionId) => {
    setLoading(true);
    try {
      const [vnResponse, voResponse] = await Promise.all([
        axios.get(`${API}/calculations`, { params: { concession_id: concessionId, limit: 100 } }),
        axios.get(`${API}/vo/calculations`, { params: { concession_id: concessionId, limit: 100 } })
      ]);
      setVnCalculations(vnResponse.data.calculations || []);
      setVoCalculations(voResponse.data.calculations || []);
    } catch (error) {
      console.error("Erreur lors du chargement de l'historique:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    const date = new Date(timestamp);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return "-";
    return `${(value * 100).toFixed(2)}%`;
  };

  // Ouvrir un calcul VN dans la calculatrice
  const openVnCalculation = (calc) => {
    // Stocker les données du calcul pour pré-remplir la calculatrice
    localStorage.setItem("emcom_edit_calculation", JSON.stringify({
      type: "vn",
      data: calc
    }));
    navigate(`/calculator/${calc.brand}`);
  };

  // Ouvrir un calcul VO dans la calculatrice
  const openVoCalculation = (calc) => {
    localStorage.setItem("emcom_edit_calculation", JSON.stringify({
      type: "vo",
      data: calc
    }));
    navigate("/calculator/vo");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="verbaere-header text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-4 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-white logo-container flex items-center justify-center shadow-md">
                <img src={logoMcom} alt="Verbaere" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <h1 className="text-xl font-heading font-black tracking-tight">e-MCom</h1>
                <p className="text-xs text-gray-400">by Verbaere Performance Suite</p>
              </div>
            </Link>
          </div>
          <div className="flex items-center gap-4">
            {session && (
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-white/10 rounded-md">
                <Building2 className="w-4 h-4 text-[#E63946]" />
                <span className="text-sm">{session.concession_name}</span>
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon"
              className="text-white hover:bg-white/10" 
              onClick={toggleTheme}
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => navigate("/")}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Retour
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-heading font-bold text-foreground flex items-center gap-3">
            <History className="w-7 h-7 text-[#E63946]" />
            Historique des Calculs
          </h2>
          <p className="text-muted-foreground mt-1">
            Retrouvez tous vos calculs de marge VN et VO
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="vn" className="flex items-center gap-2">
              <Car className="w-4 h-4" />
              VN ({vnCalculations.length})
            </TabsTrigger>
            <TabsTrigger value="vo" className="flex items-center gap-2">
              <Repeat className="w-4 h-4" />
              VO ({voCalculations.length})
            </TabsTrigger>
          </TabsList>

          {/* VN Calculations */}
          <TabsContent value="vn">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
              </div>
            ) : vnCalculations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun calcul VN enregistré</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {vnCalculations.map((calc, index) => (
                  <Card 
                    key={calc.id || index} 
                    className="hover:shadow-md transition-all cursor-pointer hover:border-[#E63946]/50 group"
                    onClick={() => openVnCalculation(calc)}
                    data-testid={`vn-calculation-${index}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Info principale */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-1 bg-[#212E38] text-white text-xs font-bold rounded">
                              {calc.brand}
                            </span>
                            <h3 className="font-heading font-bold text-foreground group-hover:text-[#E63946] transition-colors">
                              {calc.model_name}
                            </h3>
                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded">
                              {calc.sale_category}
                            </span>
                            <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(calc.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro className="w-4 h-4" />
                              Prix: {formatCurrency(calc.final_price_ttc || calc.price_ttc)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Résultats */}
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Marge Nette</p>
                            <p className={`font-mono font-bold ${(calc.net_margin_ht || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {formatCurrency(calc.net_margin_ht)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Commission</p>
                            <p className="font-mono font-bold text-[#E63946]">
                              {formatCurrency(calc.total_commission)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">% Marge</p>
                            <p className="font-mono font-bold text-foreground">
                              {calc.margin_percentage ? `${calc.margin_percentage.toFixed(2)}%` : '-'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* VO Calculations */}
          <TabsContent value="vo">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E63946]"></div>
              </div>
            ) : voCalculations.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Aucun calcul VO enregistré</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {voCalculations.map((calc, index) => (
                  <Card 
                    key={calc.id || index} 
                    className="hover:shadow-md transition-all cursor-pointer hover:border-[#E31E24]/50 group"
                    onClick={() => openVoCalculation(calc)}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        {/* Info principale */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-2 py-1 bg-[#E31E24] text-white text-xs font-bold rounded">
                              VO
                            </span>
                            <h3 className="font-heading font-bold text-foreground group-hover:text-[#E31E24] transition-colors">
                              {calc.vehicle_description || "Véhicule d'Occasion"}
                            </h3>
                            <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(calc.timestamp)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Euro className="w-4 h-4" />
                              Achat: {formatCurrency(calc.purchase_price_ht)}
                            </span>
                            <span>
                              Vente: {formatCurrency(calc.selling_price_ttc)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Résultats */}
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Marge Nette</p>
                            <p className={`font-mono font-bold ${calc.margin_ht >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                              {formatCurrency(calc.margin_ht)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">Commission</p>
                            <p className="font-mono font-bold text-[#E63946]">
                              {formatCurrency(calc.commission_total)}
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs text-muted-foreground">% Marge</p>
                            <p className="font-mono font-bold text-foreground">
                              {formatPercent(calc.margin_percent / 100)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Footer */}
      <footer className="verbaere-header text-white py-6 mt-12">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-sm text-gray-400">
            © {new Date().getFullYear()} e-MCom by Verbaere Performance Suite
          </p>
        </div>
      </footer>
    </div>
  );
}
